/**
 * Redux Store enhancer to listen to a state change.
 * Helpul for Analytics events, where we want to send an event to GA,
 * but not touch the business logic or change the state of the store.
 * Usage:
 *   const removeListener = store.stateChangeListener('USER_SIGNED_UP', (action) => {
 *     analytics.sendEvent('track', 'CompleteRegistration');
 *   });
 *
 * @param  {Funciton} createStore Redux createStore function
 * @return {Function}             Function that enhances the store
 */
export default function stateChangeListenersStoreEnhancer(createStore) {
  return (reducer, initialState, enhancer) => {
    const store = createStore(reducer, initialState, enhancer);

    const getValuesAndCallback = (state, observers, previousValues) => {
      return Object.keys(observers).reduce((obj, key) => {
        const [selector, callback] = observers[key];

        const previousValue = previousValues[key];
        const value = selector(state);

        if (previousValue !== value) {
          callback(value, previousValue, store.getState());
          obj[key] = value;
        }
        return obj;
      }, previousValues);
    };

    /**
     * Adds a bunch of listeners for store changes
     * @param  {Object}   observers         Contains a list of observers, where the key
     *                                      is the name of the observer, and the value is an array
     *                                      with the filter and the callback
     * @param  {Boolean}  invokeImmediately Invoke callbacks when the observers are defined?
     * @return {Function}                   Function that will remove the observers when called.
     */
    store.stateChangeListener = (observers, invokeImmediately = true) => {
      let values = {};

      if (invokeImmediately) {
        values = getValuesAndCallback(store, observers, values);
      }

      return store.subscribe(() =>
        getValuesAndCallback(store.getState(), observers, values)
      );
    };

    return store;
  };
}
